import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, GlyphFootprintContext, GlyphRect, hasCategory, HardwareGlyph, hitAreaOf} from "./HardwareGlyph";

// a door pull seen face-on: a vertical grip centred on the hub with a mounting post at
// each end. Sizes are typical catalogue values for a 10" centre-to-centre pull (scene
// units = mm).
const POST_SPACING = 10 * inches;        // centre to centre
const GRIP_LENGTH = 12 * inches;
const GRIP_THICKNESS = 1 * inches;
const POST_RADIUS = 0.75 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

// edge-view projections off the door face (scene units = mm).
const GRIP_PROJECTION = 2.5 * inches;    // face to the grip's outer side
const POST_WIDTH = 1 * inches;

// the grip with its posts, centred on the hub.
function footprint(ctx: GlyphFootprintContext): GlyphRect[] {
    const cx = ctx.hub.x;
    const cy = ctx.hub.y;
    const half = Math.max(GRIP_THICKNESS / 2, POST_RADIUS);
    return [{x0: cx - half, y0: cy - GRIP_LENGTH / 2, x1: cx + half, y1: cy + GRIP_LENGTH / 2}];
}

export const doorPull: HardwareGlyph = {
    id: "doorPull",
    targets: ["door"],
    matches: item => hasCategory(item, "Door Pull"),
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const cx = ctx.hub.x;
        const cy = ctx.hub.y;

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        g.drawRoundedRect(cx - GRIP_THICKNESS / 2, cy - GRIP_LENGTH / 2, GRIP_THICKNESS, GRIP_LENGTH, GRIP_THICKNESS / 2);
        g.endFill();

        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawCircle(cx, cy - POST_SPACING / 2, POST_RADIUS);
        g.drawCircle(cx, cy + POST_SPACING / 2, POST_RADIUS);
        g.endFill();

        return hitAreaOf(footprint(ctx), HIT_PAD);
    },
    footprint,
    // seen from the latch edge: the two posts standing off the face, the grip across their
    // outer ends.
    drawEdge(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const fx = ctx.face.x;
        const cy = ctx.face.y;
        const out = ctx.out;

        const gripInner = fx + out * (GRIP_PROJECTION - GRIP_THICKNESS);
        const gripOuter = fx + out * GRIP_PROJECTION;

        g.lineStyle(0);
        g.beginFill(color, alpha);
        for(const py of [cy - POST_SPACING / 2, cy + POST_SPACING / 2]) {
            g.drawRect(Math.min(fx, gripInner), py - POST_WIDTH / 2, Math.abs(gripInner - fx), POST_WIDTH);
        }
        g.endFill();

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        g.drawRoundedRect(Math.min(gripInner, gripOuter), cy - GRIP_LENGTH / 2, GRIP_THICKNESS, GRIP_LENGTH, GRIP_THICKNESS / 2);
        g.endFill();

        const x0 = Math.min(fx, gripOuter);
        return new Rectangle(x0 - HIT_PAD, cy - GRIP_LENGTH / 2 - HIT_PAD, GRIP_PROJECTION + 2 * HIT_PAD, GRIP_LENGTH + 2 * HIT_PAD);
    },
};
